import React, { useState, createElement } from 'react';
import { XIcon, MailIcon, PhoneIcon, CalendarIcon, MailOpenIcon, ReplyIcon, ArchiveIcon } from 'lucide-react';
import { ContactMessage } from './ContactContext';
interface ContactDetailsProps {
  message: ContactMessage;
  onClose: () => void;
  onStatusChange: (status: 'new' | 'read' | 'replied' | 'archived') => void;
}
export function ContactDetails({
  message,
  onClose,
  onStatusChange
}: ContactDetailsProps) {
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'read':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'replied':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'archived':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'جديدة';
      case 'read':
        return 'مقروءة';
      case 'replied':
        return 'تم الرد';
      case 'archived':
        return 'مؤرشفة';
      default:
        return 'غير معروف';
    }
  };
  const handleSendReply = () => {
    // In a real app, this would send an email to the user
    if (replyText.trim()) {
      // Update status to replied
      onStatusChange('replied');
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-24 right-1/2 transform translate-x-1/2 bg-green-100 text-green-800 px-4 py-2 rounded-full shadow-lg z-50 animate-fadeIn';
      notification.textContent = 'تم إرسال الرد بنجاح';
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.classList.add('opacity-0');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
      // Close reply form
      setShowReplyForm(false);
      setReplyText('');
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" data-id="element-643">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden" data-id="element-644">
        <div className="p-4 bg-sky-50 border-b border-sky-100 flex justify-between items-center sticky top-0 z-10" data-id="element-645">
          <h3 className="font-bold text-sky-900" data-id="element-646">تفاصيل الرسالة</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-sky-100 text-slate-500 hover:text-slate-700 transition-colors" data-id="element-647">
            <XIcon className="h-5 w-5" data-id="element-648" />
          </button>
        </div>
        <div className="overflow-y-auto p-6" style={{
        maxHeight: 'calc(90vh - 70px)'
      }} data-id="element-649">
          <div className="mb-6" data-id="element-650">
            <div className={`p-4 rounded-lg ${getStatusClass(message.status)} border flex items-center justify-between`} data-id="element-651">
              <div className="flex items-center" data-id="element-652">
                <span className="font-medium" data-id="element-653">
                  {getStatusText(message.status)}
                </span>
              </div>
              <div className="text-sm" data-id="element-654">
                تاريخ الإرسال: {formatDate(message.createdAt)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-655">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-656">
              <div className="flex items-center mb-3" data-id="element-657">
                <UserIcon className="h-5 w-5 text-slate-500 ml-2" data-id="element-658" />
                <span className="font-medium text-slate-700" data-id="element-659">الاسم:</span>
                <span className="mr-2 text-slate-900" data-id="element-660">{message.name}</span>
              </div>
              <div className="flex items-center mb-3" data-id="element-661">
                <MailIcon className="h-5 w-5 text-slate-500 ml-2" data-id="element-662" />
                <span className="font-medium text-slate-700" data-id="element-663">
                  البريد الإلكتروني:
                </span>
                <span className="mr-2 text-slate-900" data-id="element-664">{message.email}</span>
              </div>
              <div className="flex items-center" data-id="element-665">
                <PhoneIcon className="h-5 w-5 text-slate-500 ml-2" data-id="element-666" />
                <span className="font-medium text-slate-700" data-id="element-667">رقم الهاتف:</span>
                <span className="mr-2 text-slate-900" data-id="element-668">{message.phone}</span>
              </div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-669">
              <div className="flex items-center mb-3" data-id="element-670">
                <CalendarIcon className="h-5 w-5 text-slate-500 ml-2" data-id="element-671" />
                <span className="font-medium text-slate-700" data-id="element-672">
                  تاريخ الإرسال:
                </span>
                <span className="mr-2 text-slate-900" data-id="element-673">
                  {formatDate(message.createdAt)}
                </span>
              </div>
              <div className="flex items-center" data-id="element-674">
                <span className="font-medium text-slate-700" data-id="element-675">الموضوع:</span>
                <span className="mr-2 text-slate-900" data-id="element-676">{message.subject}</span>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-677">
            <h4 className="text-lg font-medium text-sky-800 mb-3" data-id="element-678">
              نص الرسالة:
            </h4>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-679">
              <p className="text-slate-800 whitespace-pre-wrap" data-id="element-680">
                {message.message}
              </p>
            </div>
          </div>
          {!showReplyForm ? <div className="flex flex-wrap gap-3" data-id="element-681">
              <button onClick={() => setShowReplyForm(true)} className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors duration-200 flex items-center" data-id="element-682">
                <ReplyIcon className="h-4 w-4 ml-2" data-id="element-683" />
                <span data-id="element-684">الرد على الرسالة</span>
              </button>
              {message.status !== 'read' && <button onClick={() => onStatusChange('read')} className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 transition-colors duration-200 flex items-center" data-id="element-685">
                  <MailOpenIcon className="h-4 w-4 ml-2" data-id="element-686" />
                  <span data-id="element-687">تحديد كمقروءة</span>
                </button>}
              {message.status !== 'archived' && <button onClick={() => onStatusChange('archived')} className="px-4 py-2 bg-slate-100 text-slate-800 rounded-md hover:bg-slate-200 transition-colors duration-200 flex items-center" data-id="element-688">
                  <ArchiveIcon className="h-4 w-4 ml-2" data-id="element-689" />
                  <span data-id="element-690">أرشفة</span>
                </button>}
            </div> : <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-id="element-691">
              <h4 className="text-lg font-medium text-sky-800 mb-3" data-id="element-692">
                الرد على الرسالة:
              </h4>
              <div className="mb-4" data-id="element-693">
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="اكتب ردك هنا..." className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none min-h-[150px]" data-id="element-694"></textarea>
              </div>
              <div className="flex justify-end gap-3" data-id="element-695">
                <button onClick={() => setShowReplyForm(false)} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 transition-colors duration-200" data-id="element-696">
                  إلغاء
                </button>
                <button onClick={handleSendReply} className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors duration-200 flex items-center" data-id="element-697">
                  <ReplyIcon className="h-4 w-4 ml-2" data-id="element-698" />
                  <span data-id="element-699">إرسال الرد</span>
                </button>
              </div>
            </div>}
          <div className="mt-8 pt-4 border-t border-slate-100" data-id="element-700">
            <button onClick={onClose} className="w-full py-2.5 rounded-md font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors duration-200" data-id="element-701">
              إغلاق
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function UserIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-702">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-703" />
      <circle cx="12" cy="7" r="4" data-id="element-704" />
    </svg>;
}